import React, { useState, useEffect } from 'react';
import { API_BASE, getFullNameByEmail } from '../App';

const InventoryCount = ({ user, token, onBack }) => {
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [countData, setCountData] = useState({});
  const [countPeriod, setCountPeriod] = useState(null);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isCountPeriodActive, setIsCountPeriodActive] = useState(false);
  const [isDraft, setIsDraft] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);


  useEffect(() => {
    fetchCountPeriod();
    fetchAssignedProducts();
  }, []);

  const fetchCountPeriod = async () => {
    try {
      const response = await fetch(`${API_BASE}/count-period`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCountPeriod(data);
        
        if (data && data.start_date && data.end_date) {
          const now = new Date();
          const startDate = new Date(data.start_date);
          const endDate = new Date(data.end_date);
          setIsCountPeriodActive(now >= startDate && now <= endDate);
          
          // Backend'den gelen sayım durumu bilgilerini kullan
          setHasSubmitted(data.has_submitted || false);
          setHasDraft(data.has_draft || false);
          setExistingSubmission(data.user_submission);
          
          if (data.user_submission) {
            setIsDraft(data.user_submission.status === 'draft');
            
            // Mevcut sayım verilerini yükle
            if (data.user_submission.submission_data) {
              try {
                const submissionData = typeof data.user_submission.submission_data === 'string' 
                  ? JSON.parse(data.user_submission.submission_data)
                  : data.user_submission.submission_data;
                setCountData(submissionData);
              } catch (parseError) {
                console.error('Sayım verisi parse hatası:', parseError);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Sayım periyodu yüklenirken hata:', error);
    }
  };

  const fetchAssignedProducts = async () => {
    try {
      const response = await fetch(`${API_BASE}/my-inventory`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedProducts(data);
        
        // Initialize count data if no existing submission
        if (!existingSubmission) {
          const initialCount = {};
          data.forEach(product => {
            initialCount[product.id] = {
              expected: 1,
              actual: '',
              notes: ''
            };
          });
          setCountData(initialCount);
        }
      }
    } catch (error) {
      console.error('Ürünler yüklenirken hata:', error);
    }
    setLoading(false);
  };

  const updateCount = (productId, field, value) => {
    // Eğer sayım gönderilmişse değişiklik yapılmasın
    if (hasSubmitted) {
      setMessage('Gönderilmiş sayım değiştirilemez!');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setCountData(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  const saveAsDraft = async () => {
    if (!countPeriod || !isCountPeriodActive) {
      setMessage('Sayım periyodu aktif değil!');
      return;
    }

    if (hasSubmitted) {
      setMessage('Bu sayım periyodu için zaten sayım gönderilmiş!');
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE}/count-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          count_period_id: countPeriod.id,
          submission_data: countData,
          status: 'draft'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Sayım taslak olarak kaydedildi!');
        setHasDraft(true);
        setExistingSubmission({
          ...existingSubmission,
          submission_data: countData,
          status: 'draft'
        });
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error || 'Hata oluştu');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
    }

    setSubmitting(false);
  };

  const submitFinal = async () => {
    if (!countPeriod || !isCountPeriodActive) {
      setMessage('Sayım periyodu aktif değil!');
      return;
    }

    if (hasSubmitted) {
      setMessage('Bu sayım periyodu için zaten sayım gönderilmiş!');
      return;
    }

    // Tüm ürünlerin sayılıp sayılmadığını kontrol et
    const incompleteCounts = assignedProducts.filter(product => {
      const count = countData[product.id];
      return !count || count.actual === '';
    });

    if (incompleteCounts.length > 0) {
      setMessage(`${incompleteCounts.length} ürün için sayım tamamlanmamış!`);
      return;
    }

    // Sayfayı en üste kaydır ve dialog'u aç
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setShowSubmitDialog(true);
    }, 100); // Smooth scroll için kısa bekleme
  };

  // Dialog onay işlemi
  const handleConfirmSubmit = async () => {
    setShowSubmitDialog(false);
    setSubmitting(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE}/count-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          count_period_id: countPeriod.id,
          submission_data: countData,
          status: 'submitted'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Sayım başarıyla gönderildi! Artık değişiklik yapamazsınız.');
        setIsDraft(false);
        setHasSubmitted(true);
        setHasDraft(false);
        setExistingSubmission({
          ...existingSubmission,
          submission_data: countData,
          status: 'submitted'
        });
        setTimeout(() => onBack(), 5000);
      } else {
        setMessage(data.error || 'Hata oluştu');
      }
    } catch (error) {
      setMessage('Bağlantı hatası');
    }

    setSubmitting(false);
  };

  // Submit dialog'u iptal et
  const handleCancelSubmit = () => {
    setShowSubmitDialog(false);
  };

  const deleteDraft = async () => {
    if (hasSubmitted) {
      setMessage('Gönderilmiş sayım silinemez!');
      return;
    }

    // Sayfayı en üste kaydır ve dialog'u aç
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setShowDeleteDialog(true);
    }, 100); // Smooth scroll için kısa bekleme
  };

  // Delete dialog onay işlemi
  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);

    try {
      const response = await fetch(`${API_BASE}/count-submission/${countPeriod.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Sayım taslağı silindi!');
        setExistingSubmission(null);
        setIsDraft(true);
        setHasDraft(false);
        
        // Reset count data
        const initialCount = {};
        assignedProducts.forEach(product => {
          initialCount[product.id] = {
            expected: 1,
            actual: '',
            notes: ''
          };
        });
        setCountData(initialCount);
        
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error || 'Silme işleminde hata oluştu!');
      }
    } catch (error) {
      setMessage('Bağlantı hatası!');
    }
  };

  // Delete dialog'u iptal et
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!countPeriod?.end_date) return null;
    const endDate = new Date(countPeriod.end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getCompletionRate = () => {
    if (assignedProducts.length === 0) return 0;
    const completedCount = assignedProducts.filter(product => {
      const count = countData[product.id];
      return count && count.actual !== '';
    }).length;
    return Math.round((completedCount / assignedProducts.length) * 100);
  };

  if (loading) {
    return <div className="container"><div className="loading">Yükleniyor...</div></div>;
  }

  return (
    <div className="container">
      <div className="form-container">
        <button onClick={onBack} className="btn-back">Geri Dön</button>
        <h2>Envanter Sayımı</h2>
        
        {/* Sayım Periyodu Bilgisi */}
        {countPeriod ? (
          <div className={`count-period-info ${isCountPeriodActive ? 'active' : 'inactive'}`}>
            <h3>{countPeriod.title}</h3>
            <p><strong>Başlangıç:</strong> {formatDate(countPeriod.start_date)}</p>
            <p><strong>Bitiş:</strong> {formatDate(countPeriod.end_date)}</p>
            {countPeriod.description && (
              <p><strong>Açıklama:</strong> {countPeriod.description}</p>
            )}
            
            {isCountPeriodActive ? (
              <div className="period-status active">
                ✅ Sayım periyodu aktif
                {getDaysRemaining() > 0 && (
                  <span className="days-remaining"> - {getDaysRemaining()} gün kaldı</span>
                )}
              </div>
            ) : (
              <div className="period-status inactive">
                ❌ Sayım periyodu aktif değil
              </div>
            )}
          </div>
        ) : (
          <div className="count-period-info inactive">
            <div className="period-status inactive">
              ⏳ Henüz aktif bir sayım periyodu belirlenmemiş
            </div>
          </div>
        )}

        {/* Kullanıcının Sayım Durumu */}
        {hasSubmitted && (
          <div className="submitted-info">
            <h4>Sayım Tamamlandı</h4>
            <p>Bu sayım periyodu için sayımınızı zaten gönderdiniz.</p>
            {existingSubmission?.submitted_at && (
              <p><strong>Gönderim Tarihi:</strong> {formatDate(existingSubmission.submitted_at)}</p>
            )}
            <p><strong>Tamamlanma Oranı:</strong> {getCompletionRate()}%</p>
            <div style={{marginTop: '1rem'}}>
              <strong>🔒 Bu sayım kalıcı olarak kaydedilmiştir ve değiştirilemez.</strong>
            </div>
          </div>
        )}

        {/* Mevcut Sayım Durumu (sadece submitted değilse) */}
        {!hasSubmitted && existingSubmission && (
          <div className={`existing-submission ${isDraft ? 'draft' : 'submitted'}`}>
            <h4>📝 Taslak Sayım</h4>
            <p>
              <strong>Son Güncelleme:</strong> {formatDate(existingSubmission.updated_at)}
            </p>
            <div className="completion-rate">
              <strong>Tamamlanma Oranı:</strong> {getCompletionRate()}% ({assignedProducts.filter(p => countData[p.id]?.actual !== '').length}/{assignedProducts.length})
            </div>
          </div>
        )}

        {!isCountPeriodActive ? (
          <div className="count-disabled">
            <h3>Sayım Yapılamıyor</h3>
            <p>Sayım işlemi sadece belirlenen tarih aralığında yapılabilir.</p>
            {countPeriod && (
              <p>
                Bir sonraki sayım periyodu: 
                <strong> {formatDate(countPeriod.start_date)} - {formatDate(countPeriod.end_date)}</strong>
              </p>
            )}
          </div>
        ) : hasSubmitted ? (
          <div className="count-completed">
            <h3>Sayım Tamamlandı</h3>
            <p>Bu sayım periyodu için sayımınız başarıyla gönderilmiş ve kaydedilmiştir.</p>
            <p>Sayım sonuçlarınızı aşağıda görüntüleyebilirsiniz:</p>
            
            {/* Tamamlanmış Sayım Sonuçları */}
            <div className="completed-count-results">
              {assignedProducts.map(product => (
                <div key={product.id} className="completed-count-item">
                  <div className="product-info">
                    <h4>{product.product_name}</h4>
                    <p>Seri No: {product.serial_code}</p>
                    <p>Ürün Kodu: {product.product_code}</p>
                  </div>
                  
                  <div className="count-result">
                    {countData[product.id]?.actual === '1' ? (
                      <span className="result-found">Mevcut</span>
                    ) : countData[product.id]?.actual === '0' ? (
                      <span className="result-missing">Kayıp</span>
                    ) : (
                      <span className="result-unknown">Bilinmiyor</span>
                    )}
                    
                    {countData[product.id]?.notes && (
                      <div className="count-notes">
                        <strong>Not:</strong> {countData[product.id].notes}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <p>Size atanan ürünlerin sayımını yapın:</p>
            
            <div className="count-actions">
              {isDraft && !hasSubmitted && (
                <>
                  <button 
                    onClick={saveAsDraft}
                    disabled={submitting}
                    className="btn-secondary"
                  >
                    {submitting ? 'Kaydediliyor...' : '💾 Taslak Kaydet'}
                  </button>
                  {hasDraft && (
                    <button 
                      onClick={deleteDraft}
                      className="btn-danger"
                    >
                      🗑️ Taslağı Sil
                    </button>
                  )}
                </>
              )}
            </div>
            
            {assignedProducts.length === 0 ? (
              <div className="no-data">Size atanan hiç ürün bulunmuyor.</div>
            ) : (
              <div className="count-list">
                {assignedProducts.map(product => (
                  <div key={product.id} className="count-item">
                    <div className="product-info">
                      <h4>{product.product_name}</h4>
                      <p>Marka/Model: {product.brand} {product.model}</p>
                      <p>Seri No: {product.serial_code}</p>
                      <p>Ürün Kodu: {product.product_code}</p>
                      <p>Beklenen: Mevcut (1 adet)</p>
                    </div>
                    
                    <div className="count-inputs">
                      <div className="form-group">
                        <label>Ürün Durumu:</label>
                        <select
                          value={countData[product.id]?.actual || ''}
                          onChange={(e) => updateCount(product.id, 'actual', e.target.value)}
                          disabled={hasSubmitted}
                        >
                          <option value="">Durum seçin...</option>
                          <option value="1">✅ Mevcut</option>
                          <option value="0">❌ Kayıp/Bulunamadı</option>
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label>Not (İsteğe bağlı):</label>
                        <input
                          type="text"
                          value={countData[product.id]?.notes || ''}
                          onChange={(e) => updateCount(product.id, 'notes', e.target.value)}
                          placeholder="Ek açıklama"
                          disabled={hasSubmitted}
                        />
                      </div>
                      
                      {countData[product.id]?.actual && (
                        <div className="count-difference">
                          <strong>
                            Durum: {parseInt(countData[product.id].actual) === 1 ? '✅ Mevcut' : '❌ Kayıp'}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {assignedProducts.length > 0 && !hasSubmitted && (
              <div className="final-actions">
                <button 
                  onClick={submitFinal}
                  disabled={submitting || getCompletionRate() < 100}
                  className="btn-primary"
                >
                  {submitting ? 'Gönderiliyor...' : '📤 Kesin Sayımı Gönder (Kalıcı)'}
                </button>
                {getCompletionRate() < 100 && (
                  <p className="completion-warning">
                    ⚠️ Tüm ürünlerin sayımını tamamlayın ({getCompletionRate()}% tamamlandı)
                  </p>
                )}
                <div className="final-warning">
                  <strong>⚠️ Uyarı:</strong> Sayımınızı gönderdikten sonra değişiklik yapılamaz ve bu sayım periyodu için tekrar sayım yapamazsınız.
                </div>
              </div>
            )}
          </>
        )}
        
        {/* Submit Confirmation Dialog */}
        {showSubmitDialog && (
          <div className="dialog-overlay">
            <div className="dialog-container submit-dialog">
              <div className="dialog-header">
                <h4>📤 Sayımı Kesin Olarak Gönder</h4>
                <button 
                  className="dialog-close" 
                  onClick={handleCancelSubmit}
                >
                  ✕
                </button>
              </div>
              
              <div className="dialog-content">
                <div className="warning-section">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-text">
                    <p><strong>Bu işlem geri alınamaz!</strong></p>
                    <p>Sayımınızı kesin olarak göndermek istediğinizden emin misiniz?</p>
                  </div>
                </div>
                
                <div className="submission-summary">
                  <h5>Gönderilecek Sayım Özeti:</h5>
                  <div className="summary-stats">
                    <div className="stat-item">
                      <span className="stat-label">Toplam Ürün:</span>
                      <span className="stat-value">{assignedProducts.length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Mevcut:</span>
                      <span className="stat-value found">{assignedProducts.filter(p => countData[p.id]?.actual === '1').length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Kayıp:</span>
                      <span className="stat-value missing">{assignedProducts.filter(p => countData[p.id]?.actual === '0').length}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Tamamlanma:</span>
                      <span className="stat-value">{getCompletionRate()}%</span>
                    </div>
                  </div>
                  
                  <div className="consequences">
                    <h6>Bu işlem sonrası:</h6>
                    <ul>
                      <li>🔒 Sayımınız kalıcı olarak kaydedilecek</li>
                      <li>❌ Değişiklik yapamayacaksınız</li>
                      <li>🚫 Bu periyotta yeniden sayım yapamayacaksınız</li>
                      <li>📊 Sonuçlar yöneticilere iletilecek</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="dialog-actions">
                <button 
                  className="btn-secondary" 
                  onClick={handleCancelSubmit}
                >
                  İptal
                </button>
                <button 
                  className="btn-primary submit-confirm-btn" 
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Gönderiliyor...' : '📤 Evet, Kesin Olarak Gönder'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Draft Dialog */}
        {showDeleteDialog && (
          <div className="dialog-overlay">
            <div className="dialog-container delete-dialog">
              <div className="dialog-header">
                <h4>🗑️ Taslağı Sil</h4>
                <button 
                  className="dialog-close" 
                  onClick={handleCancelDelete}
                >
                  ✕
                </button>
              </div>
              
              <div className="dialog-content">
                <div className="warning-section">
                  <div className="warning-icon">🗑️</div>
                  <div className="warning-text">
                    <p><strong>Sayım taslağını silmek istediğinizden emin misiniz?</strong></p>
                    <p>Bu işlem geri alınamaz ve tüm girdiğiniz veriler silinecektir.</p>
                  </div>
                </div>
                
                <div className="draft-summary">
                  <h5>Silinecek Taslak Bilgileri:</h5>
                  <ul>
                    <li><strong>Tamamlanma Oranı:</strong> {getCompletionRate()}%</li>
                    <li><strong>Toplam Ürün:</strong> {assignedProducts.length}</li>
                    <li><strong>Sayılan Ürün:</strong> {assignedProducts.filter(p => countData[p.id]?.actual !== '').length}</li>
                    <li><strong>Son Güncelleme:</strong> {existingSubmission?.updated_at ? formatDate(existingSubmission.updated_at) : 'Bilinmiyor'}</li>
                  </ul>
                </div>
              </div>
              
              <div className="dialog-actions">
                <button 
                  className="btn-secondary" 
                  onClick={handleCancelDelete}
                >
                  İptal
                </button>
                <button 
                  className="btn-danger" 
                  onClick={handleConfirmDelete}
                >
                  🗑️ Sil
                </button>
              </div>
            </div>
          </div>
        )}
        
        {message && (
          <div className={`message ${message.includes('başarı') || message.includes('kaydedildi') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryCount;